import { useEffect, useMemo, useRef, useState } from "react";

import { Link, useNavigate } from "react-router-dom";

import { Trans, useTranslation } from "react-i18next";

import { doc, getDoc, Timestamp } from "firebase/firestore";

import { signOut } from "firebase/auth";

import { httpsCallable } from "firebase/functions";

import { ref, uploadBytes } from "firebase/storage";

import { Capacitor } from "@capacitor/core";

import { ScreenOrientation } from "@capacitor/screen-orientation";

import { auth, db, functions, storage } from "../firebase";

import { buildSupportNewTicketPath } from "../support/supportRoutes";

import { useAccountSessionState } from "../context/AccountSessionStateContext";

import "./VerificationPage.css";



const ALLOWED_STATUSES = ["approved", "provisionally_approved"];

const VIDEO_RECORD_MS = 4000;



function waitForVideoDimensions(video, timeoutMs = 12000) {

  return new Promise((resolve, reject) => {

    if (video.videoWidth > 0 && video.videoHeight > 0) {

      resolve();

      return;

    }



    let timeoutId;

    const onReady = () => {

      if (video.videoWidth > 0 && video.videoHeight > 0) {

        cleanup();

        resolve();

      }

    };



    const cleanup = () => {

      clearTimeout(timeoutId);

      video.removeEventListener("loadedmetadata", onReady);

      video.removeEventListener("loadeddata", onReady);

      video.removeEventListener("resize", onReady);

    };



    timeoutId = setTimeout(() => {

      cleanup();

      reject(new Error("Video preview timed out."));

    }, timeoutMs);



    video.addEventListener("loadedmetadata", onReady);

    video.addEventListener("loadeddata", onReady);

    video.addEventListener("resize", onReady);

  });

}



function getRecorderMimeType() {

  if (typeof MediaRecorder === "undefined") {

    return "";

  }



  const candidates = [

    "video/webm;codecs=vp9",

    "video/webm;codecs=vp8",

    "video/webm",

  ];



  for (const mimeType of candidates) {

    if (MediaRecorder.isTypeSupported(mimeType)) {

      return mimeType;

    }

  }



  return "";

}



function getStatusText(status, t) {

  switch (status) {

    case "pending_auto":

      return t("verification.status.pending_auto", { defaultValue: "Your request is in initial review" });

    case "manual_review":

      return t("verification.status.manual_review", { defaultValue: "Your request is under manual review" });

    case "provisionally_approved":

      return t("verification.status.provisionally_approved", { defaultValue: "Verification is provisionally approved" });

    case "rejected":

      return t("verification.status.rejected", { defaultValue: "Verification was rejected. Please contact support" });

    case "recheck_required":

      return t("verification.status.recheck_required", { defaultValue: "Verification needs to be repeated" });

    case "suspended":

      return t("verification.status.suspended", { defaultValue: "Verification status is suspended" });

    case "approved":

      return t("verification.status.approved", { defaultValue: "Verification approved" });

    default:

      return t("verification.status.none", { defaultValue: "No verification request has been submitted yet" });

  }

}



export default function VerificationPage() {

  const { t } = useTranslation();

  // Camera-lifecycle effect reads translations through this ref, never
  // through `t` directly - see that effect's own comment for why. Kept
  // in sync on every render, including the first.
  const tRef = useRef(t);
  tRef.current = t;

  const navigate = useNavigate();

  // Success-path-only: see handleSubmit below for why this must be
  // awaited before navigate("/home") - mirrors the identical, already-
  // shipped fix for the same bug class in SelectGenderPage.js (see its
  // own "/home <-> /select-gender" comment).
  const { refresh: refreshAccountSessionState } = useAccountSessionState();

  const videoRef = useRef(null);

  // Synchronous guard against rapid duplicate submit taps: the Submit
  // button's own `disabled={submitting}` only takes effect after React
  // has re-rendered, so two taps fired in the same tick could otherwise
  // both pass handleSubmit's early checks and each independently
  // capture/record/upload - two verification requests for one photo.
  // Checked and set synchronously, before any state update or await,
  // mirroring the same pattern already used for onboarding submission
  // (see SelectGenderPage.js's submitInFlightRef).
  const submitInFlightRef = useRef(false);



  const [status, setStatus] = useState("none");

  const [loading, setLoading] = useState(true);

  const [cameraReady, setCameraReady] = useState(false);

  const [cameraError, setCameraError] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");

  const [successMessage, setSuccessMessage] = useState("");

  const [consentChecked, setConsentChecked] = useState(false);



  useEffect(() => {

    let isMounted = true;



    const load = async () => {

      try {

        const user = auth.currentUser;



        if (!user) {

          navigate("/");

          return;

        }



        const snap = await getDoc(doc(db, "users", user.uid));



        if (!isMounted) return;



        if (!snap.exists()) {

          navigate("/");

          return;

        }



        const data = snap.data() || {};



        if (data.gender !== "female") {

          navigate("/home");

          return;

        }



        const currentStatus = String(data.verificationStatus || "none");



        if (ALLOWED_STATUSES.includes(currentStatus)) {

          // Same stale-cache hazard as handleSubmit's own identical
          // branch below (see its comment for the full mechanism) -
          // this one fires on ordinary MOUNT, not just right after a
          // new submission: a returning user whose verification was
          // already approved in an earlier session (or approved by
          // this very effect on a prior mount) still has a stale
          // AccountSessionStateContext reading "needs_verification"
          // until something refreshes it. Without this, RequireGender
          // bounces back here exactly the same way.

          if (typeof refreshAccountSessionState === "function") {

            await refreshAccountSessionState();

          }

          navigate("/home");

          return;

        }



        setStatus(currentStatus);

      } catch (error) {

        console.error("VerificationPage error:", error);

        navigate("/");

        return;

      } finally {

        if (isMounted) setLoading(false);

      }

    };



    load();



    return () => {

      isMounted = false;

    };

  }, [navigate, refreshAccountSessionState]);



  useEffect(() => {

    if (!Capacitor.isNativePlatform()) return undefined;

    // Portrait lock is scoped to this screen only - never applied to
    // AndroidManifest/app-wide, so calls and other screens keep their
    // own orientation behavior. Rotating the phone mid-verification
    // previously reflowed/resized the camera preview unpredictably;
    // locking portrait for the lifetime of this component removes
    // that source of "strange behavior on rotation" without touching
    // any other screen. Best-effort only - a lock/unlock failure must
    // never block or crash verification.

    ScreenOrientation.lock({ orientation: "portrait" }).catch((err) => {

      console.warn("VerificationPage orientation lock failed:", err?.message || err);

    });



    return () => {

      ScreenOrientation.unlock().catch((err) => {

        console.warn("VerificationPage orientation unlock failed:", err?.message || err);

      });

    };

  }, []);



  // CAMERA LIFECYCLE - must run exactly once per legitimate mount (when
  // `loading` first clears), never on an ordinary re-render. This used
  // to depend on `[loading, t]`; `t` (react-i18next's translation
  // function) is not guaranteed to keep the same reference across
  // renders, so including it here meant ANY re-render that happened to
  // receive a new `t` - including ones this effect's own
  // setCameraReady/setCameraError calls directly cause - tore the
  // active stream down and reopened the camera, forever, in a tight
  // loop (confirmed live on a real device during a female verification
  // capture attempt, and reproduced as a regression test in
  // VerificationPage.test.js). Fixed by depending on `loading` alone;
  // the catch branch below still needs the LATEST translation, so it
  // reads it through tRef instead of closing over `t` directly - a ref
  // read never requires being listed as a dependency.
  useEffect(() => {

    if (loading) return undefined;



    let isMounted = true;

    let stream = null;



    const startCamera = async () => {

      setCameraReady(false);

      setCameraError("");



      try {

        stream = await navigator.mediaDevices.getUserMedia({

          video: { facingMode: "user" },

          audio: false,

        });



        if (!isMounted) {

          stream.getTracks().forEach((track) => track.stop());

          return;

        }



        const video = videoRef.current;

        if (!video) {

          throw new Error("Video element not available");

        }



        video.srcObject = stream;

        await video.play().catch((playErr) => {

          console.warn("VerificationPage video play:", playErr);

        });



        await waitForVideoDimensions(video);



        if (!isMounted) return;



        setCameraReady(true);

        setCameraError("");

      } catch (error) {

        console.error("Camera start error:", error);

        if (!isMounted) return;

        setCameraReady(false);

        setCameraError(

          tRef.current("verification.camera_error", {

            defaultValue:

              "Could not start the camera. Please allow camera access.",

          })

        );

      }

    };



    startCamera();



    return () => {

      isMounted = false;

      setCameraReady(false);

      if (stream) {

        stream.getTracks().forEach((track) => track.stop());

      }

      if (videoRef.current) {

        videoRef.current.srcObject = null;

      }

    };

  }, [loading]);



  const statusText = useMemo(() => getStatusText(status, t), [status, t]);



  const captureProfilePhotoBlob = () =>

    new Promise((resolve, reject) => {

      const video = videoRef.current;



      if (!video || !video.videoWidth || !video.videoHeight) {

        reject(

          new Error(

            t("verification.errors.video_preview_not_ready", {

              defaultValue: "Video preview is not ready.",

            })

          )

        );

        return;

      }



      const canvas = document.createElement("canvas");

      canvas.width = video.videoWidth;

      canvas.height = video.videoHeight;



      const ctx = canvas.getContext("2d");

      if (!ctx) {

        reject(

          new Error(

            t("verification.errors.capture_failed", {

              defaultValue: "Failed to capture profile photo.",

            })

          )

        );

        return;

      }



      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);



      canvas.toBlob(

        (blob) => {

          if (!blob) {

            reject(

              new Error(

                t("verification.errors.capture_failed", {

                  defaultValue: "Failed to capture profile photo.",

                })

              )

            );

            return;

          }

          resolve(blob);

        },

        "image/jpeg",

        0.92

      );

    });



  const recordVerificationVideoBlob = () =>

    new Promise((resolve, reject) => {

      const video = videoRef.current;

      const stream = video?.srcObject;



      if (!video || !stream) {

        reject(

          new Error(

            t("verification.errors.camera_stream_unavailable", {

              defaultValue: "Camera stream is not available.",

            })

          )

        );

        return;

      }



      if (typeof MediaRecorder === "undefined") {

        reject(

          new Error(

            t("verification.errors.media_recorder_unsupported", {

              defaultValue: "MediaRecorder is not supported in this browser.",

            })

          )

        );

        return;

      }



      const mimeType = getRecorderMimeType();

      const chunks = [];



      let recorder;

      try {

        recorder = mimeType

          ? new MediaRecorder(stream, { mimeType })

          : new MediaRecorder(stream);

      } catch (err) {

        reject(

          new Error(

            t("verification.errors.record_failed", {

              defaultValue: "Failed to record verification video.",

            })

          )

        );

        return;

      }



      recorder.ondataavailable = (event) => {

        if (event.data && event.data.size > 0) {

          chunks.push(event.data);

        }

      };



      recorder.onerror = () => {

        reject(

          new Error(

            t("verification.errors.record_failed", {

              defaultValue: "Failed to record verification video.",

            })

          )

        );

      };



      recorder.onstop = () => {

        if (!chunks.length) {

          reject(

            new Error(

              t("verification.errors.no_video_recorded", {

                defaultValue: "No verification video was recorded.",

              })

            )

          );

          return;

        }



        resolve(

          new Blob(chunks, {

            type: mimeType || recorder.mimeType || "video/webm",

          })

        );

      };



      try {

        recorder.start();

      } catch (err) {

        reject(

          new Error(

            t("verification.errors.record_failed", {

              defaultValue: "Failed to record verification video.",

            })

          )

        );

        return;

      }



      window.setTimeout(() => {

        if (recorder.state === "recording") {

          recorder.stop();

        }

      }, VIDEO_RECORD_MS);

    });



  const handleSupport = () => {

    navigate(buildSupportNewTicketPath({ type: "issue", category: "verification" }));

  };



  const handleSubmit = async () => {

    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    const user = auth.currentUser;



    if (!user?.uid) {

      submitInFlightRef.current = false;

      navigate("/");

      return;

    }



    if (!cameraReady || !videoRef.current?.srcObject) {

      setErrorMessage(

        t("verification.camera_not_ready", {

          defaultValue:

            "The camera is not ready yet. Please try again in a few moments.",

        })

      );

      setSuccessMessage("");

      submitInFlightRef.current = false;

      return;

    }



    if (!consentChecked) {

      setErrorMessage(

        t("verification.consent_required", {

          defaultValue:

            "Please agree to use the photo and video for verification.",

        })

      );

      setSuccessMessage("");

      submitInFlightRef.current = false;

      return;

    }



    setSubmitting(true);

    setErrorMessage("");

    setSuccessMessage("");



    try {

      const requestId =

        typeof crypto !== "undefined" && crypto.randomUUID

          ? crypto.randomUUID()

          : `verification_${Date.now()}`;



      const basePath = `verificationRequests/${user.uid}/${requestId}`;

      const profilePhotoPath = `${basePath}/profile_photo`;

      const verificationVideoPath = `${basePath}/verification_video`;



      const profilePhotoBlob = await captureProfilePhotoBlob();

      const verificationVideoBlob = await recordVerificationVideoBlob();



      await uploadBytes(ref(storage, profilePhotoPath), profilePhotoBlob, {

        contentType: "image/jpeg",

      });



      await uploadBytes(ref(storage, verificationVideoPath), verificationVideoBlob, {

        contentType: verificationVideoBlob.type || "video/webm",

      });



      const submitVerificationRequest = httpsCallable(

        functions,

        "submitVerificationRequest"

      );



      const result = await submitVerificationRequest({

        requestId,

        profilePhotoPath,

        verificationVideoPath,

        consent: {

          accepted: true,

          acceptedAt: Timestamp.now(),

          textVersion: "verification_consent_v1",

          locale: "ar",

        },

      });



      const nextStatus = result?.data?.status || "provisionally_approved";



      setStatus(nextStatus);

      setSuccessMessage(

        t("verification.request_success", {

          defaultValue: "Submitted. We'll notify you when approved.",

        })

      );



      if (ALLOWED_STATUSES.includes(nextStatus)) {

        // AccountSessionStateContext resolves access/reason ONCE
        // (keyed on [isReady, uid]) and never re-reads users/{uid} on
        // its own - so without this, its cached state still says
        // "needs_verification" the instant we navigate, RequireGender
        // (guarding /home) reads that same stale value and bounces
        // straight back to /verification, this effect's own load()
        // re-reads the now-allowed status fresh and navigates to
        // /home again, forever - a live-device-confirmed infinite
        // /verification <-> /home loop. Awaiting the refresh first
        // means the single render that follows navigate("/home")
        // already has the correct, up-to-date access/reason - the
        // exact same fix already shipped for the identical bug in
        // SelectGenderPage.js (see its own comment).

        if (typeof refreshAccountSessionState === "function") {

          await refreshAccountSessionState();

        }

        navigate("/home");

      }

    } catch (error) {

      console.error("submitVerificationRequest error:", error);

      setErrorMessage(

        error?.message ||

          t("verification.submit_failed", {

            defaultValue:

              "Could not submit the verification request. Please try again.",

          })

      );

    } finally {

      setSubmitting(false);

      submitInFlightRef.current = false;

    }

  };



  const handleLogout = async () => {

    await signOut(auth);

    navigate("/");

  };



  if (loading) return null;



  return (

    <div className="verification-page">

      <div className="verification-card">

        <div className="verification-badge">Livora</div>



        <h1 className="verification-title">

          {t("verification.title", { defaultValue: "Account verification" })}

        </h1>



        <p className="verification-message">

          {t("verification.message", {

            defaultValue: "Female accounts must be verified before using Livora.",

          })}

        </p>



        {status !== "none" ? (
          <div className="verification-status-box">

            <span className="verification-status-label">

              {t("verification.current_status", { defaultValue: "Current status" })}

            </span>

            <strong className="verification-status-text">{statusText}</strong>

          </div>
        ) : null}



        <div className="verification-camera-box">

          <div className="verification-camera-frame">

            <video

              ref={videoRef}

              className="verification-video"

              autoPlay

              muted

              playsInline

            />

            <div className="verification-face-overlay" aria-hidden="true">

              <div className="verification-face-circle" />

            </div>

          </div>



          {!cameraReady && !cameraError ? (

            <p className="verification-camera-hint">

              {t("verification.camera_preparing", {

                defaultValue: "Starting camera…",

              })}

            </p>

          ) : null}



          <p className="verification-camera-hint">

            {t("verification.camera_hint", {

              defaultValue: "Place your face in the frame, then tap Start verification.",

            })}

          </p>

        </div>



        {cameraError ? (

          <div className="verification-feedback verification-feedback-error">

            {cameraError}

          </div>

        ) : null}



        {errorMessage ? (

          <div className="verification-feedback verification-feedback-error">

            {errorMessage}

          </div>

        ) : null}



        {successMessage ? (

          <div className="verification-feedback verification-feedback-success">

            {successMessage}

          </div>

        ) : null}



        <div className="verification-consent">

          <label className="verification-consent-label">

            <input

              type="checkbox"

              className="verification-consent-checkbox"

              checked={consentChecked}

              onChange={(e) => setConsentChecked(e.target.checked)}

            />

            <span className="verification-consent-text">

              <Trans

                i18nKey="verification.legal_consent_short"

                defaults="I agree to the <termsLink>Terms of Use</termsLink> and <privacyLink>Privacy Policy</privacyLink>."

                components={{

                  termsLink: <Link to="/terms" className="livora-legal-link" />,

                  privacyLink: <Link to="/privacy" className="livora-legal-link" />,

                }}

              />

            </span>

          </label>

          <p className="verification-consent-sub">

            {t("verification.media_submission_hint", {

              defaultValue: "Photo and short video for review.",

            })}{" "}

            <button

              type="button"

              className="verification-consent-details-inline"

              onClick={() => navigate("/verification-info")}

            >

              {t("verification.read_details_short", { defaultValue: "Details" })}

            </button>

          </p>

        </div>



        <div className="verification-actions">

          <button

            type="button"

            className="verification-button verification-button-primary"

            onClick={handleSubmit}

            disabled={submitting || !cameraReady || !consentChecked}

          >

            {submitting

              ? t("verification.submitting", {

                  defaultValue: "Submitting request...",

                })

              : t("verification.start", { defaultValue: "Start verification" })}

          </button>



          <button

            type="button"

            className="verification-button verification-button-secondary"

            onClick={handleSupport}

          >

            {t("verification.contact_support", {

              defaultValue: "Contact support",

            })}

          </button>



          <button

            type="button"

            className="verification-button verification-button-tertiary"

            onClick={handleLogout}

          >

            {t("auth.logout", { defaultValue: "Log out" })}

          </button>

        </div>

      </div>

    </div>

  );

}

